import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Logo from "../../images/Logo.png";
import NetworkCanvas from "../NetworkCanvas";
import CfriGate from "./CfriGate";
import CfriSidebar from "./CfriSidebar";
import { CfriLockedError, getContent, hasPass, lock, peekContent } from "../../service/CfriService";

function initialState() {
    const content = peekContent();
    if (content) return { status: "ready", content };
    return { status: hasPass() ? "loading" : "locked" };
}

/**
 * Page shell for every CFRI portal page: the password gate, then the
 * DeepThought chrome (sidebar + header) around the page.
 *
 * Access: unlisted (nothing in the app links here; CFRI gets the /cfri URL)
 * and password-protected without an account. The password is checked by the
 * backend, which returns a pass the browser keeps; the report itself is only
 * ever fetched from the backend with that pass (see CfriService). A deep link
 * such as /cfri/proposal/<id> asks for the password, then opens that page.
 *
 * `children` is a render function `(content) => ReactNode`, as in FunderShell.
 *
 * The app's <Header/> is deliberately not used: its notification bell and user
 * menu act on a DeepThought login, and a stale one makes the token refresh
 * redirect to "/", which would throw a visitor out of the report.
 */
export default function CfriShell({ active, children }) {
    const navigate = useNavigate();
    const [state, setState] = useState(initialState);

    const load = useCallback(() => {
        const cached = peekContent();
        if (cached) {
            setState({ status: "ready", content: cached });
            return;
        }
        setState({ status: "loading" });
        getContent()
            .then((content) => setState({ status: "ready", content }))
            .catch((err) => setState(err instanceof CfriLockedError
                ? { status: "locked" }
                : { status: "error", message: err.message }));
    }, []);

    useEffect(() => {
        if (state.status === "loading") load();
        // Only on mount: later loads are started by the gate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Print in the light theme: dark-theme text is light ink meant for a dark
    // page, and it would come out faint on white paper.
    useEffect(() => {
        let theme = null;
        const before = () => {
            theme = document.documentElement.dataset.theme;
            document.documentElement.dataset.theme = "light";
        };
        const after = () => {
            if (theme) document.documentElement.dataset.theme = theme;
            theme = null;
        };
        window.addEventListener("beforeprint", before);
        window.addEventListener("afterprint", after);
        return () => {
            window.removeEventListener("beforeprint", before);
            window.removeEventListener("afterprint", after);
        };
    }, []);

    const onLock = () => {
        lock();
        setState({ status: "locked" });
        window.scrollTo(0, 0);
    };

    if (state.status === "locked") {
        return <CfriGate onUnlocked={load} />;
    }

    const content = state.content;
    return (
        <div className="home-layout cfri-layout">
            <CfriSidebar proposals={content?.proposals} active={active} onLock={onLock} />
            <main className="home-main">
                <header className="home-header cfri-no-print">
                    <img src={Logo} alt="CFRI Review" className="mobile-header-logo theme-invert" onClick={() => navigate("/cfri")} style={{ cursor: "pointer" }} />
                </header>
                <div className="home-content">
                    <div className="funder-topbar cfri-no-print">
                        <div className="funder-topbar-context">
                            <span className="funder-topbar-funder">CFRI</span>
                            <span className="funder-topbar-sep">·</span>
                            <span>Proposals on the cystic fibrosis literature</span>
                            <span className="funder-call-status cfri-confidential">Confidential</span>
                        </div>
                        {content?.overview?.built && <span className="cfri-built">{content.overview.built}</span>}
                    </div>
                    {state.status === "ready" ? children(content) : state.status === "error" ? (
                        <div className="funder-empty">
                            <p>{state.message}</p>
                            <button className="funder-btn-ghost" onClick={load}>Try again</button>
                        </div>
                    ) : (
                        <div className="cfri-loading">
                            <NetworkCanvas />
                            <p>Opening the report…</p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
